import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Navigate, Outlet } from "react-router-dom";
import type { Meeting, SchedulingPage } from "@sales-ops-amplify/shared";
import { DEFAULT_WEEKLY_HOURS } from "@sales-ops-amplify/shared";
import { cn } from "@/cn";
import { startGoogleOAuth } from "@/api/google-calendar";
import { CreateMeetingModal } from "@/components/meetings/create-meeting-modal";
import { MeetingWorkbench } from "@/components/meetings/meeting-workbench";
import { SchedulingPageModal } from "@/components/meetings/scheduling-page-modal";
import {
  useMeetingPreferences,
  useMeetings,
  useSchedulingPages,
  useSyncMeetings,
  useUpdateMeetingPreferences,
  useUpdateSchedulingPage,
} from "@/hooks/useMeetings";
import type { MeetingView } from "@/api/meetings";
import { useDebouncedValue } from "@/hooks/usePagination";

const TABS = [
  { to: "/meetings/scheduled", label: "Scheduled meetings", end: false },
  { to: "/meetings/scheduling-pages", label: "Scheduling pages", end: true },
  { to: "/meetings/availability", label: "Availability & tools", end: true },
] as const;

function bookingUrl(slug: string): string {
  return `${window.location.origin}/book/${slug}`;
}

async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    window.prompt("Copy link", text);
  }
}

function formatWhen(iso: string, timezone: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  try {
    return d.toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: timezone,
    });
  } catch {
    return d.toLocaleString();
  }
}

export function MeetingsLayout() {
  return (
    <div className="space-y-5 pb-10">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Meetings</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Schedule meetings, share booking pages, and manage availability.
        </p>
      </div>
      <nav className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end}
            className={({ isActive }) =>
              cn(
                "px-3 py-2 text-sm",
                isActive
                  ? "border-b-2 border-brand font-medium text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )
            }
          >
            {t.label}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </div>
  );
}

/** @deprecated use MeetingsLayout nested routes */
export function MeetingsPage() {
  return <Navigate to="/meetings/scheduled" replace />;
}

export function MeetingsScheduledPage() {
  const [view, setView] = useState<MeetingView>("upcoming");
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q.trim(), 300);
  const [selected, setSelected] = useState<Meeting | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const listQuery = useMeetings({ view, q: debouncedQ || undefined });
  const syncMut = useSyncMeetings();
  const prefsQuery = useMeetingPreferences();
  const meetings = listQuery.data?.meetings ?? [];

  // Drop or refresh side panel when status/view/q filters exclude the meeting.
  useEffect(() => {
    setSelected((prev) => {
      if (!prev) return prev;
      const fresh = meetings.find((m) => m.meetingId === prev.meetingId);
      return fresh ?? null;
    });
  }, [meetings]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {(
            [
              ["upcoming", "Upcoming"],
              ["past", "Past"],
              ["cancelled", "Cancelled"],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setView(id)}
              className={cn(
                "rounded-md px-3 py-1.5 text-sm",
                view === id
                  ? "bg-brand text-brand-foreground"
                  : "border border-border text-muted-foreground hover:text-foreground",
              )}
            >
              {label}
            </button>
          ))}
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search meetings…"
            className="ml-1 w-48 rounded-md border border-border bg-background px-3 py-1.5 text-sm"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={syncMut.isPending}
            onClick={() => syncMut.mutate()}
            className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted"
          >
            {syncMut.isPending ? "Syncing…" : "Sync calendar"}
          </button>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground"
          >
            Schedule meeting
          </button>
        </div>
      </div>

      {!prefsQuery.data?.calendar.connected && (
        <div className="rounded-md border border-border bg-muted/40 px-4 py-3 text-sm">
          <span className="text-muted-foreground">
            Calendar not connected — meetings still save locally.{" "}
          </span>
          <button
            type="button"
            className="text-brand hover:underline"
            onClick={async () => {
              const { url } = await startGoogleOAuth();
              if (url) window.location.href = url;
            }}
          >
            Connect Google Calendar
          </button>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        <div className="min-w-0 overflow-x-auto overscroll-x-contain rounded-lg border border-border [-webkit-overflow-scrolling:touch]">
          {listQuery.isLoading ? (
            <div className="flex justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
            </div>
          ) : meetings.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No meetings in this view. Schedule one or sync your calendar.
            </div>
          ) : (
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="border-b border-border bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">Title</th>
                  <th className="px-3 py-2 font-medium">When</th>
                  <th className="px-3 py-2 font-medium">Guests</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Join</th>
                </tr>
              </thead>
              <tbody>
                {meetings.map((m) => (
                  <tr
                    key={m.meetingId}
                    onClick={() => setSelected(m)}
                    className={cn(
                      "cursor-pointer border-b border-border last:border-0 hover:bg-muted/40",
                      selected?.meetingId === m.meetingId && "bg-muted/50",
                    )}
                  >
                    <td className="px-3 py-2.5 font-medium">{m.title}</td>
                    <td className="px-3 py-2.5 text-muted-foreground">
                      {formatWhen(m.startAt, m.timezone)}
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground">
                      {m.guests[0]?.name ||
                        m.guests[0]?.email ||
                        m.leadName ||
                        "—"}
                    </td>
                    <td className="px-3 py-2.5 capitalize">
                      {m.status.replace("_", " ")}
                    </td>
                    <td className="px-3 py-2.5">
                      {m.conferencingUrl ? (
                        <a
                          href={m.conferencingUrl}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-brand hover:underline"
                        >
                          Join
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <MeetingWorkbench
          meeting={selected}
          onClose={() => setSelected(null)}
          onUpdated={(m) => {
            setSelected(m.status === "scheduled" ? m : null);
          }}
        />
      </div>

      {createOpen && (
        <CreateMeetingModal onClose={() => setCreateOpen(false)} />
      )}
    </div>
  );
}

export function MeetingsSchedulingPagesPage() {
  const pagesQuery = useSchedulingPages();
  const updateMut = useUpdateSchedulingPage();
  const prefsQuery = useMeetingPreferences();
  const [modalPage, setModalPage] = useState<SchedulingPage | null | undefined>(
    undefined,
  );
  const [copied, setCopied] = useState<string | null>(null);
  const pages = pagesQuery.data?.pages ?? [];
  const personalSlug = prefsQuery.data?.preferences.personalSlug;

  async function onCopy(slug: string) {
    await copyText(bookingUrl(slug));
    setCopied(slug);
    setTimeout(() => setCopied(null), 1500);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Meeting types guests can book. Share a link or use your personal
          page.
        </p>
        <button
          type="button"
          onClick={() => setModalPage(null)}
          className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground"
        >
          New scheduling page
        </button>
      </div>

      {personalSlug && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-4 py-3 text-sm">
          <div>
            <p className="font-medium">Personal booking link</p>
            <p className="text-muted-foreground">/book/{personalSlug}</p>
          </div>
          <button
            type="button"
            onClick={() => void onCopy(personalSlug)}
            className="rounded-md border border-border px-3 py-1.5 hover:bg-muted"
          >
            {copied === personalSlug ? "Copied" : "Copy link"}
          </button>
        </div>
      )}

      {pagesQuery.isLoading ? (
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
        </div>
      ) : pages.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No scheduling pages yet. Create a 30-minute page to start taking
          bookings.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {pages.map((page) => (
            <div
              key={page.pageId}
              className="flex flex-col gap-3 rounded-lg border border-border p-4"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-medium">{page.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {page.durationMin} min · {page.hostMode.replace("_", " ")}
                  </p>
                </div>
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={page.active}
                    onChange={(e) =>
                      updateMut.mutate({
                        pageId: page.pageId,
                        patch: { active: e.target.checked },
                      })
                    }
                  />
                  Active
                </label>
              </div>
              <p className="text-xs text-muted-foreground">/book/{page.slug}</p>
              <div className="mt-auto flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => void onCopy(page.slug)}
                  className="rounded-md border border-border px-2.5 py-1 text-xs hover:bg-muted"
                >
                  {copied === page.slug ? "Copied" : "Copy link"}
                </button>
                <button
                  type="button"
                  onClick={() => setModalPage(page)}
                  className="rounded-md border border-border px-2.5 py-1 text-xs hover:bg-muted"
                >
                  Edit
                </button>
                <Link
                  to={`/book/${page.slug}`}
                  target="_blank"
                  className="rounded-md border border-border px-2.5 py-1 text-xs hover:bg-muted"
                >
                  Preview
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalPage !== undefined && (
        <SchedulingPageModal
          page={modalPage}
          onClose={() => setModalPage(undefined)}
        />
      )}
    </div>
  );
}

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function MeetingsAvailabilityPage() {
  const prefsQuery = useMeetingPreferences();
  const updateMut = useUpdateMeetingPreferences();
  const prefs = prefsQuery.data?.preferences;
  const [timezone, setTimezone] = useState("");
  const [personalSlug, setPersonalSlug] = useState("");
  const [brandingTitle, setBrandingTitle] = useState("");
  const [defaultCustomLocation, setDefaultCustomLocation] = useState("");
  const [message, setMessage] = useState<string | null>(null);

  const weeklyHours = useMemo(
    () => prefs?.weeklyHours ?? DEFAULT_WEEKLY_HOURS,
    [prefs?.weeklyHours],
  );

  useEffect(() => {
    if (!prefs) return;
    setTimezone(prefs.timezone);
    setPersonalSlug(prefs.personalSlug);
    setBrandingTitle(prefs.brandingTitle ?? "");
    setDefaultCustomLocation(prefs.defaultCustomLocation ?? "");
  }, [prefs]);

  async function save() {
    setMessage(null);
    try {
      await updateMut.mutateAsync({
        timezone: timezone || prefs?.timezone,
        personalSlug: personalSlug || undefined,
        brandingTitle: brandingTitle || undefined,
        defaultCustomLocation: defaultCustomLocation || undefined,
        weeklyHours,
        calendarSyncEnabled: prefs?.calendarSyncEnabled,
        createEngagementOnBook: prefs?.createEngagementOnBook,
        defaultLocationType: prefs?.defaultLocationType,
      });
      setMessage("Saved");
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Save failed");
    }
  }

  if (prefsQuery.isLoading || !prefs) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Calendar
        </h2>
        <div className="rounded-lg border border-border p-4 text-sm">
          {prefsQuery.data?.calendar.connected ? (
            <p>
              Connected via{" "}
              <span className="font-medium capitalize">
                {prefsQuery.data.calendar.provider}
              </span>
              .
            </p>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-muted-foreground">No calendar connected.</p>
              <button
                type="button"
                className="text-brand hover:underline"
                onClick={async () => {
                  const { url } = await startGoogleOAuth();
                  if (url) window.location.href = url;
                }}
              >
                Connect Google Calendar
              </button>
            </div>
          )}
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={prefs.calendarSyncEnabled}
            onChange={(e) =>
              updateMut.mutate({ calendarSyncEnabled: e.target.checked })
            }
          />
          Sync calendar events into Meetings
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={prefs.createEngagementOnBook}
            onChange={(e) =>
              updateMut.mutate({ createEngagementOnBook: e.target.checked })
            }
          />
          Create engagement when a meeting is booked
        </label>
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Booking profile
        </h2>
        <label className="block text-sm">
          <span className="text-muted-foreground">Timezone</span>
          <input
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
          />
        </label>
        <label className="block text-sm">
          <span className="text-muted-foreground">Personal slug</span>
          <div className="mt-1 flex items-center gap-2">
            <span className="text-muted-foreground">/book/</span>
            <input
              value={personalSlug}
              onChange={(e) => setPersonalSlug(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2"
            />
          </div>
        </label>
        <label className="block text-sm">
          <span className="text-muted-foreground">Branding title</span>
          <input
            value={brandingTitle}
            onChange={(e) => setBrandingTitle(e.target.value)}
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
          />
        </label>
        <label className="block text-sm">
          <span className="text-muted-foreground">Default custom location</span>
          <input
            value={defaultCustomLocation}
            onChange={(e) => setDefaultCustomLocation(e.target.value)}
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
          />
        </label>
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Weekly hours
        </h2>
        <div className="space-y-2 rounded-lg border border-border p-4">
          {DAY_LABELS.map((label, day) => {
            const win = weeklyHours.find((w) => w.dayOfWeek === day);
            return (
              <div
                key={day}
                className="flex flex-wrap items-center justify-between gap-2 text-sm"
              >
                <span className="w-10 font-medium">{label}</span>
                {win ? (
                  <span className="text-muted-foreground">
                    {win.startTime} – {win.endTime}
                  </span>
                ) : (
                  <span className="text-muted-foreground">Unavailable</span>
                )}
              </div>
            );
          })}
          <p className="pt-1 text-xs text-muted-foreground">
            Default Mon–Fri 09:00–17:00. Fine-grained hour editing ships next;
            days shown reflect saved preferences.
          </p>
        </div>
      </section>

      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={updateMut.isPending}
          onClick={() => void save()}
          className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground disabled:opacity-60"
        >
          {updateMut.isPending ? "Saving…" : "Save preferences"}
        </button>
        {message && (
          <span className="text-sm text-muted-foreground">{message}</span>
        )}
      </div>
    </div>
  );
}
